import { Theme } from '@radix-ui/themes';
import { Toaster, toast } from 'sonner';
import { usePage } from '@inertiajs/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import ErrorFallback from '@/components/ErrorFallback';
import LandingLayout from '@/components/LandingLayout';
import NoLayout from '@/components/NoLayout';
import SidebarLayout from '@/components/SidebarLayout';

function Layout({ children }) {
  const { props, url } = usePage();
  const { flash, locale, current_user, layout } = props;
  const { i18n } = useTranslation();
  const posthog = usePostHog();

  // biome-ignore lint/correctness/useExhaustiveDependencies: dismiss toast on url change
  useEffect(() => {
    toast.dismiss();
  }, [url]);

  useEffect(() => {
    i18n.changeLanguage(locale);
  }, [locale, i18n]);

  useEffect(() => {
    if (current_user) {
      posthog.identify(current_user.id, {
        email: current_user.email,
        name: current_user.name,
      });
    } else {
      posthog.reset();
    }
  }, [current_user, posthog]);

  useEffect(() => {
    if (!flash) return;

    flash.map(([type, message]) => {
      switch (type) {
        case 'notice':
          toast(message);
          break;
        case 'success':
          toast.success(message);
          break;
        case 'alert':
          toast.warning(message);
          break;
        default:
          toast[type](message);
      }
    });
  }, [flash]);

  const PageLayout = (() => {
    switch (layout) {
      case 'none':
        return NoLayout;
      case 'landing':
        return LandingLayout;
      default:
        return current_user ? SidebarLayout : NoLayout;
    }
  })();

  return (
    <Theme className="h-full" accentColor="mint">
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <PostHogProvider
          apiKey={window.POSTHOG_API_KEY_FRONTEND}
          options={{
            api_host: 'https://us.i.posthog.com',
          }}
        >
          <PageLayout>{children}</PageLayout>
          <Toaster position="bottom-right" />
        </PostHogProvider>
      </Sentry.ErrorBoundary>
    </Theme>
  );
}

const AppLayout = (page) => <Layout>{page}</Layout>;

export default AppLayout;
